/**
 * Map-readiness guard — keep a failed basemap from taking the data layers
 * down with it (REQ-UI-166).
 *
 * # The failure this exists to prevent
 *
 * :file:`Map.tsx` gates **every** data layer (platform markers, detections,
 * tracks, alerts, annotations, coverage) on ``loadedMap``, which is set from
 * :func:`use-map.useMap`'s ``onMapReady``, which is wired to MapLibre's
 * ``load`` event. MapLibre fires ``load`` only once ``Style.loaded()`` is
 * true, and *that* requires every source's ``TileManager`` to report
 * ``loaded()``. So a basemap whose TileJSON never arrives can hold ``load``
 * forever — and the operator gets a completely blank display, with no
 * basemap *and no markers*, while the platform data is arriving perfectly
 * well over SSE.
 *
 * The confirmed trigger is a Content-Security-Policy that does not
 * allow-list the tile host the BFF advertises (REQ-UI-097): the browser
 * refuses the connection, MapLibre's source load rejects, and the map is
 * dead. It was observed live in PR #1040's "before" case — the Platforms
 * panel listed 51 platforms against an empty map.
 *
 * # Why this cannot be left to MapLibre
 *
 * Whether a failed basemap kills the map is, today, an accident of the
 * MapLibre version — a one-line difference in ``VectorTileSource.load()``:
 *
 * .. code-block:: js
 *
 *    // maplibre-gl@4.7.1 — _loaded stays false ⇒ dead map
 *    catch (err) { this._tileJSONRequest = null; this.fire(new ErrorEvent(err)); }
 *
 *    // maplibre-gl@6.x — _loaded set BEFORE the fire ⇒ degrades correctly
 *    catch (err) { this._tileJSONRequest = null; this._loaded = true;
 *                  if (!isAbortError(err)) this.fire(new ErrorEvent(ensureError(err))); }
 *
 * ``TileManager`` does ``_sourceErrored = this._source.loaded()`` in its
 * error handler and short-circuits ``TileManager.loaded()`` on that flag, so
 * the v6 assignment is the whole difference between "markers survive" and
 * "blank screen". The dependency has already been moved across that
 * boundary twice in two months (4.7.1 → 6.1 → 6.2 → 4.7.1 → 6.5 → 6.7 →
 * 6.10 → 6.7), and nothing in Meridian would have caught the regression.
 * This module makes the degradation **Meridian's** behaviour rather than an
 * inherited upstream accident.
 *
 * # The shape of the guard
 *
 * A watchdog, not an error-triggered promotion. Promoting the moment a
 * source error arrives would fire on every transient tile 500 and would
 * change the healthy ordering; waiting for a deadline instead means the
 * healthy path is byte-for-byte unchanged (``load`` cancels the watchdog
 * before it can run) and the degraded path covers *every* reason ``load``
 * fails to arrive — a CSP block, a black-holed host that never errors at
 * all, or a future upstream change nobody predicted.
 *
 * Observed source errors are recorded only to make the diagnostic specific
 * (:data:`MapDegradedInfo.sourceId` / ``message``), never to trigger it.
 *
 * Cross-references:
 *   - REQ-UI-166 (basemap failure degrades to background + markers with one
 *     operator-visible diagnostic).
 *   - REQ-UI-035 / REQ-UI-097 (tile routing + the per-deployment CSP).
 *   - ADR-016 amended 2026-06-10 (browser-direct basemap); #1002.
 *   - CLAUDE.md §8.1 (operator visibility; fail-safe, not fail-blank).
 *   - map/viewport-sync.ts (the ``install…`` → disposer idiom followed here).
 */
import { MAP_BASE_SOURCE_ID } from "@/map/map-constants";
import { VECTOR_BASEMAP_SOURCE_ID } from "@/map/style-vector";

/**
 * How long to wait for MapLibre's ``load`` before declaring the basemap
 * degraded and mounting the data layers anyway.
 *
 * The trade-off is bounded on both sides. Too short pre-empts a slow but
 * healthy basemap on a congested classified link and raises a false alarm;
 * too long leaves the operator staring at an empty display during an
 * incident. Six seconds sits past any plausible TileJSON round-trip
 * (REQ-UI-004 budgets the *snapshot* render at 500 ms) while keeping the
 * blank-display window to something an operator reads as "slow", not
 * "broken".
 *
 * This is a code default, not deployment configuration: it is a property of
 * the failure mode rather than of the network, and an operator has no basis
 * on which to tune it.
 */
export const DEFAULT_BASEMAP_READY_TIMEOUT_MS = 6_000;

/**
 * The basemap source ids a failure may be attributed to — the raster source
 * and the vector source. These are the only two a Meridian style creates for
 * the basemap (ADR-016 amended 2026-06-10); a failure on any other source id
 * belongs to a data layer and is never reported as a basemap failure.
 */
export const BASEMAP_SOURCE_IDS: readonly string[] = [MAP_BASE_SOURCE_ID, VECTOR_BASEMAP_SOURCE_ID];

/** Why the map had to be promoted without MapLibre's ``load``. */
export type MapDegradedReason =
  /** A basemap source raised an error — CSP block, DNS failure, 4xx/5xx. */
  | "basemap-source-error"
  /** ``load`` simply never arrived, with no error to explain it. */
  | "no-load-event";

/**
 * What the degraded promotion reports to its caller. Carries enough for an
 * operator-facing message *and* for an engineer reading the console from a
 * deployment they cannot SSH into (CLAUDE.md §5.4).
 */
export interface MapDegradedInfo {
  /** The classification of the failure. */
  readonly reason: MapDegradedReason;
  /** The failing basemap source id, when one reported an error. */
  readonly sourceId?: string;
  /** The first failure's message, when one was reported. */
  readonly message?: string;
  /** The watchdog deadline that expired, for log correlation. */
  readonly timeoutMs: number;
}

/**
 * MapLibre's ``data`` / ``error`` events, in the shape this module reads.
 *
 * ``sourceId`` is attached by ``Style.addSource``'s evented-parent
 * decorator, so it is present on anything originating in a source's
 * ``TileManager``. ``tile`` is present on a **tile-level** event and absent
 * on a **source-level** one — the discriminator that separates "one tile
 * failed" from "the whole TileJSON failed".
 */
export interface BasemapReadinessSourceEvent {
  readonly dataType?: string;
  readonly sourceId?: string;
  readonly tile?: unknown;
  readonly error?: { readonly message?: string };
}

/** The error-event slice. Same shape; named for the call site's clarity. */
export type BasemapReadinessErrorEvent = BasemapReadinessSourceEvent;

/**
 * The slice of MapLibre's :class:`Map` this module uses. Structural, with
 * literal event names, so tests can pass a minimal double — the same
 * approach :type:`viewport-sync.ViewportSyncMapLike` takes.
 */
export interface BasemapReadinessMapLike {
  on(eventName: "load", handler: () => void): unknown;
  on(eventName: "error", handler: (event: BasemapReadinessErrorEvent) => void): unknown;
  on(eventName: "data", handler: (event: BasemapReadinessSourceEvent) => void): unknown;
  off(eventName: "load", handler: () => void): unknown;
  off(eventName: "error", handler: (event: BasemapReadinessErrorEvent) => void): unknown;
  off(eventName: "data", handler: (event: BasemapReadinessSourceEvent) => void): unknown;
}

/** The two callbacks :func:`installBasemapReadiness` drives. */
export interface BasemapReadinessHandlers {
  /**
   * Promote the map to ready. Called **exactly once** per install, from
   * whichever of ``load`` / the watchdog comes first.
   */
  readonly onReady: () => void;
  /**
   * Report that readiness was reached without a working basemap. Called at
   * most once, immediately *before* ``onReady`` so the operator-visible
   * diagnostic and the markers appear together.
   */
  readonly onDegraded: (info: MapDegradedInfo) => void;
}

/**
 * Install the readiness watchdog on ``map``.
 *
 * Wires ``load`` to ``onReady`` as usual, and arms a ``timeoutMs`` deadline
 * that promotes the map anyway — reporting through ``onDegraded`` first — if
 * ``load`` never arrives. Promotion is idempotent: a ``load`` that turns up
 * after the deadline is ignored, so ``onMapReady``'s one-time work (glyph
 * registration, the initial viewport seed) never runs twice.
 *
 * @param map - The MapLibre map (or test double).
 * @param handlers - The promotion + degradation callbacks.
 * @param timeoutMs - Watchdog deadline. Defaults to
 *     :const:`DEFAULT_BASEMAP_READY_TIMEOUT_MS`.
 * @returns A cleanup function. Callers MUST invoke it on unmount to remove
 *     the listeners and cancel the pending watchdog.
 */
export function installBasemapReadiness(
  map: BasemapReadinessMapLike,
  handlers: BasemapReadinessHandlers,
  timeoutMs: number = DEFAULT_BASEMAP_READY_TIMEOUT_MS,
): () => void {
  let promoted = false;
  let reported = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // First basemap failure only. The raster path fires one error per *tile* —
  // a single blocked host produced 8 in the live probe — and the operator
  // must get one diagnostic, not 8.
  let firstFailure: { sourceId: string; message?: string } | null = null;
  // Basemap tiles that actually rendered. MapLibre fires a source ``data``
  // event carrying ``tile`` for each one (verified against 6.7.0: a healthy
  // raster source produced 8, a CSP-blocked one produced 0). This is what
  // separates "the basemap is gone" from "one tile blipped".
  let renderedTiles = 0;

  const cancelWatchdog = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** Promote once. Later callers are no-ops. */
  const promote = (): void => {
    if (promoted) {
      return;
    }
    promoted = true;
    handlers.onReady();
  };

  /** Report once. Later callers are no-ops. */
  const report = (info: MapDegradedInfo): void => {
    if (reported) {
      return;
    }
    reported = true;
    handlers.onDegraded(info);
  };

  const isBasemap = (sourceId: string | undefined): boolean =>
    sourceId !== undefined && BASEMAP_SOURCE_IDS.includes(sourceId);

  const onLoad = (): void => {
    // Promote immediately, exactly as before this guard existed. The
    // watchdog deliberately keeps running: on maplibre-gl 6.x a blocked
    // basemap still lets ``load`` fire, so the *health* verdict cannot be
    // decided here.
    promote();
  };

  const onError = (event: BasemapReadinessErrorEvent): void => {
    // Recorded for the diagnostic's detail — an error never reports on its
    // own, because a transient tile 500 on an otherwise-working basemap must
    // not raise an operator banner. The deadline decides.
    if (firstFailure !== null || !isBasemap(event?.sourceId)) {
      return;
    }
    firstFailure = {
      // Narrowed by ``isBasemap`` above.
      sourceId: event.sourceId as string,
      message: event?.error?.message,
    };
  };

  const onData = (event: BasemapReadinessSourceEvent): void => {
    // A source ``data`` event carrying a ``tile`` is a tile that rendered.
    // Only the basemap's own count matters — the snapshot source rendering
    // must never be read as a working basemap.
    if (event?.dataType !== "source" || event?.tile === undefined) {
      return;
    }
    if (isBasemap(event?.sourceId)) {
      renderedTiles += 1;
    }
  };

  map.on("load", onLoad);
  map.on("error", onError);
  map.on("data", onData);

  timer = setTimeout(() => {
    timer = null;
    // The basemap is broken when it errored and put nothing on screen. This
    // is true whether or not ``load`` fired, which is the case that matters
    // on maplibre-gl 6.x: the map loads, the markers render, and without
    // this the operator is never told the background is missing.
    const basemapBroken = firstFailure !== null && renderedTiles === 0;
    if (basemapBroken) {
      report({
        reason: "basemap-source-error",
        sourceId: firstFailure?.sourceId,
        message: firstFailure?.message,
        timeoutMs,
      });
    } else if (!promoted) {
      // No error to explain it — a black-holed tile host whose fetch neither
      // resolves nor rejects, or an upstream change nobody predicted.
      report({ reason: "no-load-event", timeoutMs });
    }
    promote();
  }, timeoutMs);

  return () => {
    cancelWatchdog();
    map.off("load", onLoad);
    map.off("error", onError);
    map.off("data", onData);
  };
}
